import { useState, useEffect } from "react";
import { adminGetSettings, adminUpdateSetting } from "@/api";
import { toast } from "sonner";
import { Settings, Loader2, Save, Percent, IndianRupee, Users, Shield, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import SystemHealthPanel from "./SystemHealthPanel";

const CATEGORY_CONFIG = {
  pricing:  { label: "Subscription Pricing", icon: Layers, color: "amber", description: "Tiered subscription fee based on customer's loan amount" },
  interest: { label: "Interest Rates", icon: Percent, color: "blue", description: "Configure monthly interest rates for different tenures" },
  fees: { label: "Fees & Charges", icon: IndianRupee, color: "green", description: "Processing fees and penalty charges" },
  limits: { label: "Loan Limits", icon: Shield, color: "purple", description: "Minimum and maximum loan amounts" },
  agent: { label: "Agent Settings", icon: Users, color: "teal", description: "Commission rates and agent configuration" },
};

export default function AdminSettings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editValues, setEditValues] = useState({});
  const { role } = useAuth();
  const isAdmin = role === "super_admin" || role === "ops_staff" || role === "admin";

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await adminGetSettings();
      const s = res.data.settings || [];
      setSettings(s);
      const vals = {};
      s.forEach(item => { vals[item.key] = item.value; });
      setEditValues(vals);
    } catch (err) {
      toast.error("Failed to load settings");
    } finally { setLoading(false); }
  };

  const handleSave = async (key) => {
    const newValue = editValues[key];
    const original = settings.find(s => s.key === key);
    if (original && original.value === newValue) {
      toast.info("No changes to save");
      return;
    }
    setSaving(key);
    try {
      await adminUpdateSetting(key, newValue);
      toast.success(`${key.replace(/_/g, ' ')} updated successfully`);
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update setting");
    } finally { setSaving(null); }
  };

  const grouped = {};
  settings.forEach(s => {
    if (!grouped[s.category]) grouped[s.category] = [];
    grouped[s.category].push(s);
  });

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1565C0]" /></div>;
  }

  return (
    <div className="fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Cabinet Grotesk, Outfit, sans-serif'}}>Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Configure platform parameters and business rules</p>
      </div>

      <div className="space-y-6">
        {isAdmin && <SystemHealthPanel />}
        {Object.entries(CATEGORY_CONFIG).map(([cat, config]) => {
          const items = grouped[cat] || [];
          if (items.length === 0) return null;
          const Icon = config.icon;
          
          const colorMap = {
            blue: "bg-blue-50 border-blue-100 text-blue-600",
            green: "bg-green-50 border-green-100 text-green-600",
            purple: "bg-purple-50 border-purple-100 text-purple-600",
            teal: "bg-teal-50 border-teal-100 text-teal-600",
            amber: "bg-amber-50 border-amber-100 text-amber-600",
          };

          return (
            <div key={cat} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className={`px-6 py-4 border-b flex items-center gap-3 ${colorMap[config.color] || ''}`}>
                <Icon className="h-5 w-5" />
                <div>
                  <h3 className="font-bold text-sm">{config.label}</h3>
                  <p className="text-xs opacity-80">{config.description}</p>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {items.map(item => (
                  <div key={item.key} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <label className="text-sm font-semibold text-[#0A2540] capitalize">{item.key.replace(/_/g, ' ')}</label>
                      {item.description && <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Input
                        value={editValues[item.key] || ''}
                        onChange={e => setEditValues({...editValues, [item.key]: e.target.value})}
                        className="rounded-xl w-full sm:w-32 text-right font-semibold"
                      />
                      <span className="text-xs text-gray-400 w-8">
                        {cat === 'interest' || cat === 'fees' || (cat === 'agent' && item.key.includes('rate')) ? '%' : '₹'}
                      </span>
                      <Button
                        onClick={() => handleSave(item.key)}
                        disabled={saving === item.key}
                        size="sm"
                        className="bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-lg shrink-0"
                      >
                        {saving === item.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
